import {
  DEFAULT_DATE_FORMAT_FNS,
  EventsStatuses,
  FilterKeys,
  GetEventsQueryFilters,
} from '@schooly/api';
import {
  getSelectedItemsWithGrouping,
  SelectedItem,
  SelectedItemWithGrouping,
} from '@schooly/components/filters';
import { EventsInvite, SchoolUserRole } from '@schooly/constants';
import { useAgeGroups } from '@schooly/hooks/use-school-properties';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

export type UseEventsFiltersProps = {
  schoolId: string;
  filters: GetEventsQueryFilters;
  onSetFilters: (filters: GetEventsQueryFilters) => void;
};

export const useEventsFilters = ({ filters, onSetFilters, schoolId }: UseEventsFiltersProps) => {
  const { formatMessage } = useIntl();
  const ageGroups = useAgeGroups({
    schoolId,
    userType: SchoolUserRole.Student,
  });
  const { getAgeGroupById, schoolLevelsWithAgeGroupsMap } = ageGroups;
  const actions = useMemo(() => {
    const clearFilter = (key: keyof typeof filters) => () => {
      onSetFilters({
        ...filters,
        [key]: [],
      });
    };

    const onSetEventStatus = (v: EventsStatuses[]) => {
      onSetFilters({
        ...filters,
        [FilterKeys.EventStatus]: v,
      });
    };

    const onSetInviteType = (v: EventsInvite[]) => {
      onSetFilters({
        ...filters,
        [FilterKeys.InviteType]: v,
      });
    };

    const onSetAgeGroup = (v: string[]) => {
      onSetFilters({
        ...filters,
        [FilterKeys.AgeGroup]: v,
      });
    };

    const onSetHouse = (v: string[]) => {
      onSetFilters({
        ...filters,
        [FilterKeys.House]: v,
      });
    };

    const onSetGroup = (v: string[]) => {
      onSetFilters({
        ...filters,
        [FilterKeys.Group]: v,
      });
    };

    const onSetRecurrenceId = (v: string[]) => {
      onSetFilters({
        ...filters,
        [FilterKeys.RecurrenceId]: v,
      });
    };

    return {
      onSetDate: (v: [Date, Date]) =>
        onSetFilters({
          ...filters,
          [FilterKeys.Date]: [
            format(v[0], DEFAULT_DATE_FORMAT_FNS),
            format(v[1], DEFAULT_DATE_FORMAT_FNS),
          ],
        }),
      onSetEventStatus,
      onClearEventStatus: clearFilter(FilterKeys.EventStatus),
      onSetInviteType,
      onClearInviteType: clearFilter(FilterKeys.InviteType),
      onSetAgeGroup,
      onClearAgeGroup: clearFilter(FilterKeys.AgeGroup),
      onSetHouse,
      onClearHouse: clearFilter(FilterKeys.House),
      onSetGroup,
      onClearGroup: clearFilter(FilterKeys.Group),
      onSetRecurrenceId,
      onClearRecurrenceId: clearFilter(FilterKeys.RecurrenceId),
    };
  }, [filters, onSetFilters]);

  const filtersDate = filters.date;
  const filtersEventStatus = filters.event_status;
  const filtersInviteType = filters.invite_type;
  const filtersAgeGroup = filters.age_group;
  const filtersHouse = filters.house;
  const filtersGroup = filters.group;
  const filtersRecurrenceId = filters.recurrence_id;

  const labels = {
    [FilterKeys.Date]: formatMessage({ id: 'schoolProperty-DateAndPeriod' }),
    [FilterKeys.EventStatus]: formatMessage({ id: 'events-status' }),
    [FilterKeys.InviteType]: formatMessage({ id: 'event-participation' }),
    [FilterKeys.AgeGroup]: formatMessage({ id: 'schoolProperty-AgeGroup' }),
    [FilterKeys.House]: formatMessage({ id: 'schoolProperty-House' }),
    [FilterKeys.Group]: formatMessage({ id: 'events-group' }),
    [FilterKeys.RecurrenceId]: formatMessage({ id: 'events-recurring' }),
  };

  const selectedItemsForAgeGroups: SelectedItemWithGrouping[] = useMemo(() => {
    const selectedAgeGroups =
      filtersAgeGroup?.reduce<SelectedItem[]>((acc, id) => {
        const ageGroup = getAgeGroupById(id);

        return ageGroup
          ? [
              ...acc,
              {
                id: ageGroup.id,
                groupId: ageGroup.level_id,
              },
            ]
          : acc;
      }, []) ?? [];

    return getSelectedItemsWithGrouping(selectedAgeGroups, schoolLevelsWithAgeGroupsMap);
  }, [filtersAgeGroup, schoolLevelsWithAgeGroupsMap, getAgeGroupById]);

  return {
    ...actions,
    ...ageGroups,
    labels,
    selectedItemsForAgeGroups,
    filtersDate,
    filtersEventStatus,
    filtersInviteType,
    filtersAgeGroup,
    filtersHouse,
    filtersGroup,
    filtersRecurrenceId,
  };
};
